import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Auth, Invitations } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 8;
  const valid =
    !!token &&
    !!firstName.trim() &&
    !!lastName.trim() &&
    password.length >= 8 &&
    password === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !valid) return;
    setErr(null);
    setBusy(true);
    try {
      const user = await Invitations.accept(token, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      });
      // Sign the new user in immediately with the password they just set, so they
      // land in the app as themselves — instead of falling back to whatever session
      // already exists in this browser (e.g. the admin who created the invite).
      try {
        const { access_token } = await Auth.login(user.email, password);
        setSession(access_token, user);
        nav("/pipeline", { replace: true });
      } catch {
        // Account was created but auto sign-in failed — fall back to manual login.
        nav("/login", {
          replace: true,
          state: { notice: "Account created — please sign in." },
        });
      }
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md panel p-8">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/favicon.svg"
            alt="LeadZeppelin"
            width={36}
            height={36}
            className="w-9 h-9 rounded-lg shadow-[0_6px_20px_rgba(94,176,255,0.28)]"
          />
          <div>
            <div className="text-lg font-semibold">LeadZeppelin</div>
            <div className="text-sm text-mut-300">by Techmellon</div>
          </div>
        </div>

        <h1 className="text-xl font-semibold mb-1">Accept your invitation</h1>
        <p className="text-mut-300 text-md mb-6">Set up your account to get started.</p>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label">First name</label>
            <input
              className="input"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Last name</label>
            <input
              className="input"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>

        <label className="label">Password</label>
        <input
          className="input mb-1"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {tooShort && (
          <div className="mb-2 text-xs text-accent-pink">
            Password must be at least 8 characters.
          </div>
        )}

        <label className="label mt-2">Confirm password</label>
        <input
          className="input mb-1"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {mismatch && <div className="mb-2 text-xs text-accent-pink">Passwords do not match.</div>}

        {err && <div className="my-3 text-sm text-accent-pink">{err}</div>}

        <button
          type="submit"
          className="btn btn-primary w-full justify-center mt-4"
          disabled={busy || !valid}
        >
          {busy ? "Creating account…" : "Create account"}
        </button>

        <p className="text-xs text-mut-500 mt-5 leading-relaxed">
          Already have an account?{" "}
          <button
            type="button"
            className="text-accent-azure hover:underline"
            onClick={() => nav("/login")}
          >
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}
